import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, Filter, LogOut, Search, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { aiTrainerClient } from '@/lib/aiTrainerClient';
import { useAiTrainerAuth } from '@/contexts/AiTrainerAuthContext';
import { CorpusHero } from './Hero';
import { KPIStrip } from './KPIStrip';
import { DropZone } from './DropZone';
import { PaperCard } from './PaperCard';
import { PendingSheet } from './PendingSheet';
import { DetailDrawer } from './DetailDrawer';
import {
  type CorpusStats, type CourseOption, type Paper,
  type PaperStatus, type PendingUpload,
} from './types';

const PROCESSING: PaperStatus[] = ['queued', 'parsing', 'chunking', 'embedding'];

type StatusFilter = 'all' | 'ready' | 'processing' | 'failed';

/**
 * ExamCorpusUpload — examiner-facing surface for training the
 * institution's self-hosted question-generation model.
 *
 * Drop PDFs → tag them in a slide-up sheet → backend ingests
 * (parse → chunk → embed → pgvector) → cards in the library light up.
 * Tap a card to open the detail drawer and trigger question generation
 * or reprocess / remove.
 *
 * Backend contract:
 *   GET  /exam-corpus/papers
 *   GET  /exam-corpus/stats
 *   POST /exam-corpus/papers          multipart: file=PDF, meta=JSON
 *   POST /exam-corpus/papers/:id/reprocess
 *   POST /exam-corpus/papers/:id/delete
 */
const ExamCorpusUpload: React.FC = () => {
  const { user, logout } = useAiTrainerAuth();
  const navigate = useNavigate();

  const [papers, setPapers] = useState<Paper[]>([]);
  const [stats, setStats] = useState<CorpusStats>({
    total_papers: 0, ready: 0, processing: 0, failed: 0,
    total_chunks: 0, total_questions: 0,
  });
  // Trainers don't have an LMS-style course catalog yet — the
  // PendingSheet falls back to a free-text course_code input when
  // this list is empty. Wire to /v1/courses/recent when that ships.
  const courses: CourseOption[] = [];
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [detail, setDetail] = useState<Paper | null>(null);

  /* ── Fetchers ───────────────────────────────────────────────── */
  const fetchPapers = async () => {
    setLoading(true);
    try {
      const r = await aiTrainerClient.get('/papers');
      if (r.data?.success) setPapers(r.data.data?.papers || []);
    } catch {
      setPapers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const r = await aiTrainerClient.get('/stats');
      if (r.data?.success) setStats(r.data.data);
    } catch { /* keep zeros */ }
  };

  // Trainers don't have access to the LMS course catalog — the
  // PendingSheet falls back to a free-text "course code" field when
  // the suggestions list is empty. Future enhancement: ai_training
  // can expose /v1/courses/recent built from past uploads.
  useEffect(() => {
    fetchPapers();
    fetchStats();
  }, [user?.id]);

  // Poll every 6 s while anything is processing.
  useEffect(() => {
    const hasProcessing = papers.some((p) => PROCESSING.includes(p.status));
    if (!hasProcessing) return;
    const t = window.setInterval(() => { fetchPapers(); fetchStats(); }, 6000);
    return () => window.clearInterval(t);
  }, [papers]);

  /* ── Pending uploads ────────────────────────────────────────── */
  const acceptFiles = (files: FileList | File[]) => {
    const next: PendingUpload[] = [];
    Array.from(files).forEach((f) => {
      if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
        toast.error(`${f.name}: only PDFs are accepted`);
        return;
      }
      if (f.size > 30 * 1024 * 1024) {
        toast.error(`${f.name}: max 30 MB per file`);
        return;
      }
      next.push({
        id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        file: f,
        progress: 0,
      });
    });
    if (!next.length) return;
    setPending((prev) => [...prev, ...next]);
    setSheetOpen(true);
  };

  const patchPending = (id: string, patch: Partial<PendingUpload>) =>
    setPending((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const removePending = (id: string) =>
    setPending((prev) => prev.filter((p) => p.id !== id));

  const submitPending = async () => {
    const ready = pending.filter((p) => p.course_id);
    if (!ready.length) {
      toast.error('Pick a course for every paper before uploading');
      return;
    }
    for (const p of ready) {
      const fd = new FormData();
      fd.append('file', p.file);
      fd.append('meta', JSON.stringify({
        course_id: p.course_id,
        year: p.year,
        paper_type: p.paper_type || 'Final',
        academic_session: p.academic_session,
      }));
      try {
        await aiTrainerClient.post('/papers', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            patchPending(p.id, { progress: pct });
          },
        });
        removePending(p.id);
      } catch (err: any) {
        patchPending(p.id, { error: err?.response?.data?.message || 'Upload failed' });
      }
    }
    toast.success('Uploads queued — ingestion runs in the background');
    fetchPapers();
    fetchStats();
    if (!pending.some((p) => !p.course_id)) setSheetOpen(false);
  };

  /* ── Library actions ────────────────────────────────────────── */
  const reprocess = async (id: string) => {
    try {
      await aiTrainerClient.post(`/papers/${id}/reprocess`);
      toast.success('Reprocess queued');
      fetchPapers();
    } catch { toast.error('Failed to reprocess'); }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Remove this paper from the training corpus?')) return;
    try {
      await aiTrainerClient.post(`/papers/${id}/delete`);
      toast.success('Removed');
      setDetail(null);
      fetchPapers();
      fetchStats();
    } catch { toast.error('Failed to remove'); }
  };

  const generateFrom = (id: string) => {
    // Generation UI ships in a follow-up release — for now we just
    // hint that the paper is ready to be drawn from.
    toast.message('Generation surface coming soon', {
      description: `Paper ${id.slice(0, 8)} is ready for the generator.`,
    });
  };

  /* ── Derived ────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return papers
      .filter((p) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'ready') return p.status === 'ready';
        if (statusFilter === 'failed') return p.status === 'failed';
        return PROCESSING.includes(p.status); // 'processing'
      })
      .filter((p) => {
        if (!q) return true;
        return `${p.file_name} ${p.course_code ?? ''} ${p.course_title ?? ''} ${p.year ?? ''}`
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
  }, [papers, search, statusFilter]);

  const processingCount = papers.filter((p) => PROCESSING.includes(p.status)).length;

  return (
    <div
      className="min-h-screen min-w-0 overflow-x-clip bg-slate-50 px-3 pb-20 pt-3 text-slate-900 sm:px-5 sm:pt-5 lg:px-6 lg:pt-6"
      style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
    >
      {/* Identity bar — trainer chip + sign-out. Lives ABOVE the brand
          hero so the page reads "you are signed in as X, here's the
          training surface". */}
      {user && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2 rounded-full bg-white px-3 py-1.5 ring-1 ring-slate-200">
            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1a5276] to-[#0e9172] text-[10px] font-bold text-white">
              {(user.full_name || user.email).slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 truncate text-[12.5px] font-semibold text-slate-700">
              {user.full_name}
            </span>
            <span className="hidden truncate text-[11px] text-slate-400 sm:inline">
              · {user.email}
            </span>
          </div>
          <button
            type="button"
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-bold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      )}

      <CorpusHero
        processingCount={processingCount}
        loading={loading}
        onRefresh={() => { fetchPapers(); fetchStats(); }}
        onUpload={() => {
          // Programmatically trigger the same file picker the drop zone owns.
          const input = document.querySelector<HTMLInputElement>('input[type="file"][accept="application/pdf,.pdf"]');
          input?.click();
        }}
      />

      <KPIStrip stats={stats} />

      <DropZone onFiles={acceptFiles} />

      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
          <Search size={13} className="flex-shrink-0 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by file, course or year…"
            className="min-w-0 flex-1 border-none bg-transparent text-[13.5px] text-slate-900 outline-none"
          />
        </label>
        <div className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
          <Filter size={12} className="mx-1.5 text-slate-400" />
          {(['all', 'ready', 'processing', 'failed'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`
                rounded-lg px-3 py-1.5 text-[12px] font-bold capitalize transition
                ${statusFilter === s
                  ? 'bg-[#1a5276] text-white'
                  : 'bg-transparent text-slate-600 hover:bg-slate-50'}
              `}
            >
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Library */}
      {loading ? (
        <EmptyState icon={<Activity size={20} />} text="Loading corpus…" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Sparkles size={20} className="text-[#0e9172]" />}
          text={papers.length
            ? 'No papers match your filter.'
            : 'Your corpus is empty — upload your first past paper to get started.'}
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <PaperCard
              key={p.id}
              paper={p}
              onOpen={() => setDetail(p)}
              onReprocess={() => reprocess(p.id)}
              onGenerate={() => generateFrom(p.id)}
            />
          ))}
        </div>
      )}

      {/* Slide-up: pending uploads */}
      <PendingSheet
        open={sheetOpen}
        pending={pending}
        courses={courses}
        onClose={() => setSheetOpen(false)}
        onClear={() => { setPending([]); setSheetOpen(false); }}
        onSubmit={submitPending}
        onRemove={removePending}
        onPatch={patchPending}
      />

      {/* Side drawer: per-paper detail */}
      <DetailDrawer
        paper={detail}
        onClose={() => setDetail(null)}
        onReprocess={reprocess}
        onRemove={remove}
        onGenerate={generateFrom}
      />
    </div>
  );
};

const EmptyState: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-8 text-center text-[13px] text-slate-500">
    {icon}
    <p className="m-0">{text}</p>
  </div>
);

export default ExamCorpusUpload;
